import { useState, useMemo } from "react";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { newsItems } from "@/mocks/newsData";
import SocialShare from "@/components/feature/SocialShare";

const categoryColors: Record<string, string> = {
  publication: "bg-tie-teal/10 text-tie-teal",
  conference: "bg-tie-navy/10 text-tie-navy",
  announcement: "bg-amber-50 text-amber-700",
  milestone: "bg-emerald-50 text-emerald-700",
};

const sourceBrandColors: Record<string, string> = {
  "Textile Today": "bg-tie-teal/10 text-tie-teal border-tie-teal/20",
  "Nagorik TV": "bg-red-50 text-red-700 border-red-200",
  "Ekattor TV": "bg-red-50 text-red-700 border-red-200",
  "Jago News 24": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Dhaka Tribune": "bg-slate-100 text-slate-700 border-slate-300",
  "The Financial Express": "bg-sky-50 text-sky-700 border-sky-200",
};

function getSourceColor(source: string) {
  if (sourceBrandColors[source]) return sourceBrandColors[source];
  return "bg-white/90 text-tie-navy border-white/50";
}

function getYouTubeThumbnail(url: string) {
  let id = "";
  const match = url.match(/[?&]v=([^&]+)/);
  if (match) {
    id = match[1];
  } else {
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) id = shortMatch[1];
  }
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
}

function isVideoUrl(url: string) {
  return url.includes("youtube.com") || url.includes("youtu.be");
}

export default function MediaGrid() {
  const [activeSource, setActiveSource] = useState("all");
  const [visibleCount, setVisibleCount] = useState(12);

  const sources = useMemo(() => {
    const map = new Map<string, number>();
    newsItems.forEach((item) => {
      const s = item.source || "Other";
      map.set(s, (map.get(s) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }, []);

  const filtered =
    activeSource === "all"
      ? newsItems
      : newsItems.filter((n) => n.source === activeSource);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <ScrollReveal>
            <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
                <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                  / Coverage by Source
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-tie-text-primary leading-tight">
                All Media Coverage
              </h2>
              <p className="mt-3 md:mt-4 text-base text-tie-text-secondary">
                Filter by media outlet to see coverage from Bangladesh&apos;s leading press, digital platforms, and industry publications.
              </p>
            </div>
          </ScrollReveal>

          {/* Source Filter Chips */}
          <ScrollReveal delay={100}>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10 md:mb-14">
              <button
                onClick={() => {
                  setActiveSource("all");
                  setVisibleCount(12);
                }}
                className={`inline-flex items-center gap-1.5 px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeSource === "all"
                    ? "bg-tie-navy text-white"
                    : "bg-tie-grey text-tie-text-secondary hover:bg-tie-grey-dark"
                }`}
              >
                <i className="ri-global-line" />
                All Media
                <span className="ml-1 px-1.5 py-0.5 rounded-md bg-white/20 text-[10px] font-semibold">
                  {newsItems.length}
                </span>
              </button>
              {sources.map((source) => (
                <button
                  key={source.name}
                  onClick={() => {
                    setActiveSource(source.name);
                    setVisibleCount(12);
                  }}
                  className={`inline-flex items-center gap-1.5 px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer border ${
                    activeSource === source.name
                      ? "bg-tie-navy text-white border-tie-navy"
                      : "bg-white text-tie-text-secondary hover:bg-tie-grey border-tie-grey-dark"
                  }`}
                >
                  {source.name}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${
                    activeSource === source.name ? "bg-white/20 text-white" : "bg-tie-grey text-tie-text-muted"
                  }`}>
                    {source.count}
                  </span>
                </button>
              ))}
            </div>
          </ScrollReveal>

          {/* Media Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {visible.map((item, index) => (
              <ScrollReveal key={item.id} delay={index * 80}>
                <article className="group h-full bg-tie-grey-light rounded-2xl overflow-hidden border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  {/* Image or Video Placeholder */}
                  <a
                    href={item.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block relative h-44 md:h-52 overflow-hidden"
                  >
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : isVideoUrl(item.externalUrl) ? (
                      <>
                        <img
                          src={getYouTubeThumbnail(item.externalUrl)}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
                            <i className="ri-play-fill text-2xl text-white" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-tie-navy/80 to-tie-navy flex items-center justify-center">
                        <div className="text-center px-4">
                          <i className="ri-newspaper-line text-3xl text-white/40 mb-2" />
                          <p className="text-xs text-white/60">{item.source}</p>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-tie-navy/40 to-transparent" />
                    <div className="absolute top-3 left-3">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${categoryColors[item.category]}`}
                      >
                        {item.tag}
                      </span>
                    </div>
                    {item.source && (
                      <div className="absolute top-3 right-3">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm">
                          <i className="ri-external-link-line text-[10px] text-white" />
                        </span>
                      </div>
                    )}
                  </a>

                  {/* Content */}
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 text-xs text-tie-text-muted mb-3">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        {item.date}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-time-line" />
                        {item.readTime}
                      </span>
                    </div>
                    <a
                      href={item.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <h3 className="text-base md:text-lg font-semibold text-tie-text-primary mb-2 leading-snug group-hover:text-tie-teal transition-colors">
                        {item.title}
                      </h3>
                    </a>
                    <p className="text-sm text-tie-text-secondary leading-relaxed line-clamp-3">
                      {item.excerpt}
                    </p>
                    <div className="mt-4 pt-4 border-t border-tie-grey-dark flex items-center justify-between flex-wrap gap-3">
                      <a
                        href={item.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm font-medium text-tie-teal hover:underline"
                      >
                        Read article
                        <i className="ri-external-link-line ml-1.5 text-xs opacity-60" />
                      </a>
                      <SocialShare
                        url={item.externalUrl}
                        title={item.title}
                        excerpt={item.excerpt}
                        compact
                      />
                    </div>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <ScrollReveal>
              <div className="mt-10 md:mt-12 text-center">
                <button
                  onClick={() => setVisibleCount((c) => c + 12)}
                  className="inline-flex items-center justify-center px-8 py-3.5 rounded-full bg-tie-navy text-white font-medium text-sm hover:bg-tie-navy/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Load More Coverage
                  <i className="ri-arrow-down-line ml-2" />
                </button>
              </div>
            </ScrollReveal>
          )}

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-tie-grey flex items-center justify-center">
                <i className="ri-newspaper-line text-2xl text-tie-text-muted" />
              </div>
              <p className="text-base text-tie-text-secondary">
                No coverage from this source yet.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}